import { useEffect, useState } from 'react'

/**
 * How long a one-time code we email or text stays valid, in seconds.
 *
 * `OTP_CODE_TTL_SECONDS` mirrors the API's `OTP_TTL_SECONDS`
 * (`apps/api/src/domain/constants/otp.constants.ts`, `OTP_TTL_MS = 600000`),
 * which governs BOTH:
 *
 *   - the sign-in code, via `LoginOtpService.send` — "store it (10 min TTL,
 *     overwriting any prior in-flight code — newest wins on resend)";
 *   - phone verification, via `RequestPhoneVerificationUseCase` passing
 *     `validityPeriodSeconds: OTP_TTL_SECONDS` to Twilio Verify.
 *
 * ⚠️ **It is NOT WorkOS's number, despite the endpoint being called
 * `magic-auth`.** That naming is vestigial and actively misleading: WorkOS
 * Magic Auth is DISABLED in the WorkOS dashboard (`createMagicAuth` throws
 * `authentication_method_not_allowed`), and `AuthWorkOSController.sendMagicAuthCode`
 * says so in as many words — "BOTH branches now use our OWN OTP (generate +
 * email + verify ourselves)". The customer branch calls
 * `loginOtpService.send('owner', …)` and the admin branch
 * `workosAuthService.sendLoginOtpCode`, which is the same service again.
 *
 * That check is the whole reason this constant is allowed to exist. A
 * countdown is a MEASUREMENT shown to a user, and had the code's lifetime
 * actually belonged to WorkOS, 10 minutes would have been a plausible-looking
 * fabrication — a number that agrees with a constant in this repo and with
 * nothing that governs the code in the user's inbox. If the send path is ever
 * moved back to a provider that owns the TTL, this constant must become a
 * value the server RETURNS, not one the client assumes.
 *
 * `TWO_FACTOR_TICKET_TTL_SECONDS` mirrors `CHALLENGE_TTL_SECONDS`
 * (`two-factor-challenge.service.ts`). Note it bounds the TICKET, not the
 * code: a TOTP code from an authenticator rotates every 30s on the device and
 * has no server-side deadline of its own, but the login ticket it is spent
 * against dies at 5 minutes either way — so that is the deadline worth
 * showing. A resend REFRESHES the ticket, capped at
 * `CHALLENGE_MAX_LIFETIME_SECONDS` (15 min) overall.
 */
export const OTP_CODE_TTL_SECONDS = 600
export const TWO_FACTOR_TICKET_TTL_SECONDS = 300

export interface CodeExpiry {
  /** Whole seconds left, floored at 0. 0 whenever `expired`. */
  secondsRemaining: number
  /** True once the window has elapsed. False while `issuedAt` is null. */
  expired: boolean
  /** `M:SS`, e.g. `9:58`. `0:00` once expired. */
  formatted: string
  /** False when no code is outstanding, i.e. `issuedAt` is null. */
  active: boolean
}

const format = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

/**
 * Counts a one-time code down to its expiry.
 *
 * @param issuedAt `Date.now()` captured when the code was SENT — and captured
 *   again on every resend, since a resend overwrites the stored code and
 *   restarts its TTL ("newest wins on resend"). `null` means no code is
 *   outstanding, which reports `active: false` and never expires.
 * @param ttlSeconds one of the two constants above.
 *
 * ## Clock skew is not a threat here, and that is worth stating
 *
 * The obvious objection to a client-side countdown is that a wrong device
 * clock locks someone out of a code the server would still accept. It does not
 * apply: `issuedAt` and every tick are read from the SAME `Date.now()`, so the
 * value displayed is a delta on one clock and the device's offset from real
 * time cancels exactly. Only drift ACCUMULATED during the ten minutes could
 * matter, which is microseconds.
 *
 * What the countdown genuinely cannot see is the server deleting the code
 * early — single-use burn on a successful verify, or a resend superseding it.
 * Both are cases where the user has already moved on, so a stale countdown on
 * a dead code is not a state anyone sits in.
 *
 * ## Why it recomputes rather than decrements
 *
 * Browsers throttle `setInterval` in a backgrounded tab to roughly once a
 * minute. A counter that decremented by one per tick would therefore under-run
 * by however long the user spent in their mail app — which is precisely where
 * they go after asking for a code. Deriving the remaining time from the wall
 * clock on every tick means returning to the tab shows the true figure
 * immediately, and the only artefact of throttling is a late repaint.
 */
export const useCodeExpiry = (
  issuedAt: number | null,
  ttlSeconds: number,
): CodeExpiry => {
  const remainingAt = (now: number): number =>
    issuedAt === null ? ttlSeconds : Math.max(0, Math.ceil((issuedAt + ttlSeconds * 1000 - now) / 1000))

  const [secondsRemaining, setSecondsRemaining] = useState(() => remainingAt(Date.now()))

  useEffect(() => {
    if (issuedAt === null) {
      setSecondsRemaining(ttlSeconds)
      return
    }

    // Recompute immediately as well as on the interval: a remount, or a new
    // `issuedAt` after a resend, must not show the previous value for a second.
    setSecondsRemaining(remainingAt(Date.now()))

    const id = setInterval(() => {
      const next = remainingAt(Date.now())
      setSecondsRemaining(next)
      // Nothing changes after zero, so stop burning a timer.
      if (next === 0) clearInterval(id)
    }, 1000)

    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuedAt, ttlSeconds])

  const active = issuedAt !== null

  return {
    secondsRemaining,
    expired: active && secondsRemaining === 0,
    formatted: format(secondsRemaining),
    active,
  }
}
